import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { DoorOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DEMO_EMAIL, enterDemo } from "@/lib/demo.functions";
import { getSupabase } from "@/lib/supabase-lazy";
import { cn } from "@/lib/utils";

/**
 * One click into the live app with a stocked sample account. No signup, no card,
 * and nothing anyone types survives the nightly reset.
 */
export function DemoDoor({
  className,
  label = "Peek inside — live demo",
}: {
  className?: string;
  label?: string;
}) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function open() {
    if (busy) return;
    setBusy(true);
    try {
      const supabase = await getSupabase();
      const { data: current } = await supabase.auth.getSession();
      const currentEmail = current.session?.user?.email?.toLowerCase();
      if (currentEmail && currentEmail !== DEMO_EMAIL) {
        const ok = window.confirm(
          "You're signed in to your own account. Opening the demo will sign you out — you'll need to sign back in afterwards. Continue?",
        );
        if (!ok) {
          setBusy(false);
          return;
        }
      }
      const { email, password } = await enterDemo();
      await supabase.auth.signOut();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);
      await navigate({ to: "/app/today" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open the demo.");
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      className={cn(
        "inline-flex items-center gap-2 rounded-sm border border-primary/50 bg-primary/10 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-primary/20 disabled:opacity-60",
        className,
      )}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <DoorOpen className="h-4 w-4" aria-hidden="true" />
      )}
      {busy ? "Opening the door…" : label}
    </button>
  );
}
